import { useState, useEffect } from 'react';
import { onSnapshot, query, collection, orderBy, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db, uploadPDF, deleteFile } from '../config/firebase';

export const useRapports = () => {
  const [rapports, setRapports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    const q = query(collection(db, 'rapports'), orderBy('date', 'desc'));
    
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          date: doc.data().date.toDate ? doc.data().date.toDate() : new Date(doc.data().date)
        }));
        setRapports(data);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Erreur Firestore:', err);
        if (err.code === 'permission-denied') {
          setError('Permission refusée. Vérifiez les règles Firestore.');
        } else {
          setError(err.message);
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const createRapport = async (data, pdfFile) => {
    try {
      let pdfData = null;
      
      // Upload du PDF si fourni
      if (pdfFile) {
        setUploadProgress(10);
        pdfData = await uploadPDF(pdfFile, 'rapports', (progress) => {
          setUploadProgress(Math.round(progress));
        });
        setUploadProgress(100);
      }

      await addDoc(collection(db, 'rapports'), {
        ...data,
        pdfUrl: pdfData.url || null,
        pdfPath: pdfData.path || null,
        fileSize: pdfData.size || pdfFile.size || 0,
        date: new Date(),
        views: 0
      });
      
      setUploadProgress(0);
      return { success: true };
    } catch (err) {
      console.error('Erreur création rapport:', err);
      setUploadProgress(0);
      return { success: false, error: err.message };
    }
  };

  const editRapport = async (id, data, pdfFile, deleteOldPdf = false) => {
    try {
      const rapportDoc = doc(db, 'rapports', id);
      let updateData = { ...data };

      // Si on doit supprimer l'ancien PDF ou uploader un nouveau
      if (pdfFile) {
        // Supprimer l'ancien PDF si existe
        if (deleteOldPdf && data.oldPdfPath) {
          try {
            await deleteFile(data.oldPdfPath);
          } catch (e) {
            console.warn('Ancien PDF non trouvé:', e);
          }
        }

        // Upload nouveau PDF
        setUploadProgress(10);
        const pdfData = await uploadPDF(pdfFile, 'rapports');
        setUploadProgress(100);
        
        updateData.pdfUrl = pdfData.url;
        updateData.pdfPath = pdfData.path;
        updateData.fileSize = pdfData.size;
      }

      delete updateData.oldPdfPath;
      await updateDoc(rapportDoc, updateData);
      
      setUploadProgress(0);
      return { success: true };
    } catch (err) {
      console.error('Erreur modification rapport:', err);
      setUploadProgress(0);
      return { success: false, error: err.message };
    }
  };

  const removeRapport = async (id, pdfPath) => {
    try {
      // Supprimer le PDF du Storage si existe
      if (pdfPath) {
        try {
          await deleteFile(pdfPath);
        } catch (e) {
          console.warn('Fichier non trouvé dans Storage:', e);
        }
      }

      const rapportDoc = doc(db, 'rapports', id);
      await deleteDoc(rapportDoc);
      return { success: true };
    } catch (err) {
      console.error('Erreur suppression rapport:', err);
      return { success: false, error: err.message };
    }
  };

  return {
    rapports,
    loading,
    error,
    uploadProgress,
    createRapport,
    editRapport,
    removeRapport
  };
};

// Types de rapports
export const TYPES_RAPPORT = [
  { value: 'annuel', label: 'Rapport Annuel' },
  { value: 'semestriel', label: 'Rapport Semestriel' },
  { value: 'trimestriel', label: 'Rapport Trimestriel' },
  { value: 'activite', label: 'Rapport d\'Activité' },
  { value: 'special', label: 'Rapport Spécial' }
];
